/**
 * Jurisdiction configuration — single source of truth shared with the backend.
 *
 * All data comes from the JSON config files in `shared/jurisdictions/`.
 * The backend's Python registry and this frontend module both read from
 * the same JSON.
 */

import ukLitigation from '@jurisdictions/uk_litigation.json';
import ukArbitration from '@jurisdictions/uk_arbitration.json';
import usLitigation from '@jurisdictions/us_litigation.json';
import usArbitration from '@jurisdictions/us_arbitration.json';
import { isUsRegion } from './regions';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UiLabels {
  claimant_side: string;
  respondent_side: string;
  claimant_side_singular: string;
  respondent_side_singular: string;
  claimant_side_plural: string;
  respondent_side_plural: string;
  claimant_side_badge: string;
  respondent_side_badge: string;
  bundle_singular: string;
  bundle_plural: string;
  bundle_create_new: string;
  bundle_create_title: string;
  bundle_create_hint: string;
  bundle_settings_hint: string;
  bundle_overview_hint: string;
  bundle_name_label: string;
  bundle_title_placeholder: string;
  bundle_description_placeholder: string;
  bundle_back: string;
  bundle_none_found: string;
  bundle_none_yet: string;
  bundle_none_match: string;
  bundle_search_placeholder: string;
  bundle_go_to: string;
  bundle_delete_title: string;
  bundle_generate_pdf_title: string;
  bundle_filename_placeholder: string;
  bundle_generation_started: string;
  bundle_update_success: string;
  bundle_create_failed: string;
  bundle_add_failed: string;
  bundle_save_failed: string;
  bundle_delete_failed: string;
  bundle_already_in: string;
  bundle_folder_autocreate: string;
  case_partner_empty_hint: string;
}

export interface JurisdictionConfig {
  form_options: {
    governing_law: string[];
    venues: string[];
    client_roles: string[];
    causes_of_action: string[];
    arbitration_rules?: string[];
    seats_of_arbitration?: string[];
  };
  ui_labels: UiLabels;
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

const JURISDICTIONS: Record<string, JurisdictionConfig> = {
  uk_litigation: ukLitigation as JurisdictionConfig,
  uk_arbitration: ukArbitration as JurisdictionConfig,
  us_litigation: usLitigation as JurisdictionConfig,
  us_arbitration: usArbitration as JurisdictionConfig,
};

// Keep in sync with backend/app/jurisdictions/registry.py::_resolve_key
function resolveKey(region: string, forum?: string | null): string {
  const isArb = forum?.toLowerCase() === 'arbitration';
  if (isUsRegion(region)) return isArb ? 'us_arbitration' : 'us_litigation';
  return isArb ? 'uk_arbitration' : 'uk_litigation';
}

export function getJurisdiction(region: string, forum?: string | null): JurisdictionConfig {
  return JURISDICTIONS[resolveKey(region, forum)];
}
